import { useAuth } from '../../contexts/AuthContext';
import { useState } from 'react';
import { Alert, Autocomplete, Box, Button, FormControl, FormLabel, Typography } from '@mui/joy';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { useDebounce } from '../../hooks/useDebounce';

export function BillingRecordPicker({
  label,
  endpoint,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  endpoint: string;
  value?: number | null;
  onChange: (record: any | null) => void;
  disabled?: boolean;
}) {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const query = useDebounce(search, 250);
  const list = useInfiniteQuery({
    queryKey: ['billing-picker', user?.organization, user?.id, endpoint, query],
    initialPageParam: 1,
    enabled: !disabled,
    queryFn: async ({ pageParam }) => {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('search', query);
      url.searchParams.set('page', String(pageParam));
      const { data } = await apiClient.get<any>(url.pathname + url.search);
      return Array.isArray(data) ? { results: data, next: null, count: data.length } : data;
    },
    getNextPageParam: (last, pages) => (last.next ? pages.length + 1 : undefined),
  });
  const selected = useQuery({
    queryKey: ['billing-picker-record', user?.organization, user?.id, endpoint, value],
    enabled: !!value,
    queryFn: async () =>
      (await apiClient.get<any>(`${endpoint.split('?')[0].replace(/\/$/, '')}/${value}/`)).data,
  });
  const rows = list.data?.pages.flatMap((page) => page.results) || [];
  const options = Array.from(
    new Map(
      [...rows, ...(selected.data ? [selected.data] : [])].map((row) => [row.id, row]),
    ).values(),
  );
  return (
    <FormControl disabled={disabled}>
      <FormLabel>{label}</FormLabel>
      <Autocomplete
        options={options}
        value={options.find((row) => row.id === value) || null}
        loading={list.isLoading}
        filterOptions={(rows) => rows}
        onInputChange={(_, text, reason) => {
          if (reason === 'input' || reason === 'clear') setSearch(text);
        }}
        getOptionLabel={(row) =>
          row.company_name ||
          row.document_number ||
          row.name ||
          [row.first_name, row.last_name].filter(Boolean).join(' ') ||
          [row.job_number || `Job ${row.id}`, row.address_1, row.city].filter(Boolean).join(' · ')
        }
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_, row) => onChange(row)}
        placeholder={`Search ${label.toLowerCase()}…`}
      />
      {list.isError || selected.isError ? (
        <Alert color="danger">
          Unable to load choices.{' '}
          <Button
            size="sm"
            onClick={() => {
              list.refetch();
              selected.refetch();
            }}
          >
            Retry
          </Button>
        </Alert>
      ) : null}
      {list.hasNextPage && (
        <Box>
          <Button
            size="sm"
            variant="plain"
            loading={list.isFetchingNextPage}
            onClick={() => list.fetchNextPage()}
          >
            Load more choices
          </Button>
          <Typography level="body-xs">
            {rows.length} of {list.data?.pages[0].count} matches
          </Typography>
        </Box>
      )}
    </FormControl>
  );
}
